import { ApiDef_AdvisorSession, StorageKey_AdvisorLastEncounterIds } from '@app/advisor-shared';
import { apiWithQuery, ModuleFE_XHR, Storage_AppVersion, StorageKey } from '@nu-art/thunderstorm-frontend';
import { HeaderKey_AppVersion } from '@nu-art/thunderstorm-shared';
import { BadImplementationException, currentTimeMillis, Module, Second, sortArray } from '@nu-art/ts-common';
import { ModuleFE_Session, SessionKey_FE } from '@nu-art/user-account-frontend';
import { AdvisorRuntimeModules } from '../../consts.js';
export const SessionKey_OrganizationId = new SessionKey_FE('organizationId');
export const SessionKey_EncounterId = new SessionKey_FE('encounterId');
export const SessionKey_PatientId = new SessionKey_FE('patientId');
export const SessionKey_EHRInstanceId = new SessionKey_FE('ehrInstanceId');
export const SessionKey_EHREnvironmentId = new SessionKey_FE('environmentId');
export const SessionKey_EHRInstallationId = new SessionKey_FE('ehrInstallationId');
export const SessionKey_FacilityId = new SessionKey_FE('facilityId');
export const SessionKey_ProviderId = new SessionKey_FE('providerId');
export const SessionKey_Provider = new SessionKey_FE('provider');
const SessionKey_IsAvatar = new SessionKey_FE('isAvatar');
export const SessionKey_AdvisorConfig = new SessionKey_FE('advisorConfig');
const StorageKeyGetter_encounterLastUpdated = (encounterId) => `${encounterId}-encounter-last-updated`;
export const StorageKey_LastEncounterIds = new StorageKey(StorageKey_AdvisorLastEncounterIds).withstandDeletion().onChange(async (after, before) => {
    if (ModuleFE_AdvisorSession_Class.IAMTHEFRAME || ModuleFE_AdvisorSession.getIsAvatar())
        return;
    ModuleFE_AdvisorSession.updateEncounterTimestamp();
});
export class ModuleFE_AdvisorSession_Class extends Module {
    artifactMetadata;
    static IAMTHEFRAME = false;
    _v1;
    patientFilter = (item) => {
        return item.patientId === SessionKey_PatientId.get();
    };
    encounterFilter = (item) => {
        return item.encounterId === SessionKey_EncounterId.get();
    };
    providerFilter = (item) => {
        return item.providerId === SessionKey_ProviderId.get();
    };
    providerAndEncounterFilter = (item) => {
        return this.providerFilter(item) && this.encounterFilter(item);
    };
    isAvatar = false;
    _lastSeenAt;
    _hasRecapItems = false; //Does this run have recap items to show as reported by EM.
    constructor() {
        super();
        this._v1 = {
            validateAdvisorSession: apiWithQuery(ApiDef_AdvisorSession._v1.validateAdvisorSession),
        };
    }
    setArtifactMetadata = (artifactMetadata) => {
        this.artifactMetadata = artifactMetadata;
    };
    set lastSeenAt(val) {
        if (this._lastSeenAt)
            throw new BadImplementationException('Setting lastSeenAt only allowed once');
        const now = Date.now();
        this._lastSeenAt = {
            previous: val,
            launch: now,
            current: now,
        };
    }
    get lastSeenAt() {
        return this._lastSeenAt;
    }
    get hasRecapItems() {
        return this._hasRecapItems;
    }
    set hasRecapItems(hasRecapItems) {
        this._hasRecapItems = hasRecapItems;
    }
    debug_SetLastSeenAt = (ts) => {
        if (!this._lastSeenAt)
            return;
        this._lastSeenAt.previous = ts;
    };
    updateCurrentLastSeen = () => {
        if (!this._lastSeenAt)
            return;
        this._lastSeenAt.current = Date.now();
    };
    __onSessionUpdated() {
        this.onNewEncounter();
        // Here we replace the IDBCache instances with ones whose key includes the encounter id.
        AdvisorRuntimeModules.getRuntimeModules().forEach(module => module.onAdvisorLaunched?.());
    }
    updateEncounterTimestamp = () => {
        const encounterId = SessionKey_EncounterId.get();
        if (!encounterId)
            throw new BadImplementationException(`Failed to get encounterId to update timestamp???....`);
        new StorageKey(StorageKeyGetter_encounterLastUpdated(encounterId)).set(currentTimeMillis());
    };
    /**
     * Adds this encounter to the StorageKey of running encounters and triggers cleaning of old encounters from IDB.
     */
    onNewEncounter = () => {
        // avatar mode shouldn't effect any of these
        if (this.getIsAvatar())
            return;
        const appVersion = Storage_AppVersion.get();
        if (!appVersion)
            this.logErrorBold('App version is missing');
        else
            ModuleFE_XHR.addDefaultHeader(HeaderKey_AppVersion, appVersion);
        const encounterId = SessionKey_EncounterId.get();
        this.updateEncounterTimestamp();
        const existingEncounters = StorageKey_LastEncounterIds.get([]);
        //Check if already in the encounter list
        if (existingEncounters.includes(encounterId))
            return;
        //Check the encounter list in a delay, after all encounters had time to update their timestamps.
        setTimeout(async () => {
            await this.checkForInactiveEncounters();
        }, 10 * Second);
        //Add self to list
        existingEncounters.push(encounterId);
        //Setting this storage key triggers all other encounters to update their timestamps.
        StorageKey_LastEncounterIds.set(existingEncounters);
    };
    checkForInactiveEncounters = async () => {
        const existingEncounterIds = StorageKey_LastEncounterIds.get([]);
        //If under the limit, fail fast
        if (existingEncounterIds.length <= this.config.numOfEncountersToKeepInIDB)
            return;
        //Sort according to timestamp, starting with lowest(oldest)
        const sortedEncountersByActivity = sortArray(existingEncounterIds.map(_encounterId => {
            return {
                id: _encounterId,
                timestamp: new StorageKey(StorageKeyGetter_encounterLastUpdated(_encounterId)).get(0)
            };
        }), item => item.timestamp);
        //
        const encounterIds = sortedEncountersByActivity.map(encounterActivity => encounterActivity.id);
        //This leaves encounterIds with the encounters ids we keep, and encountersToDelete with the encounter ids we delete
        const encountersToDelete = encounterIds.splice(0, encounterIds.length - this.config.numOfEncountersToKeepInIDB);
        StorageKey_LastEncounterIds.set(encounterIds);
        //Delete the excess encounters
        await Promise.all(encountersToDelete.map(async (encounterIdToDelete) => {
            await this.cleanEncounterFromIDB(encounterIdToDelete);
        }));
    };
    /**
     *
     * @param encounterId seek the relevant patientEncounter across the IDB according to this id.
     * @param clearCache true when resetting encounter, false when not. Whether to clear cache and reset the last sync timestmap and collection module's data status.
     */
    cleanEncounterFromIDB = async (encounterId, clearCache = false) => {
        this.logWarning(`Clearing encounter ${encounterId} from IDB${!clearCache ? '' : ', including resetting cache and data status(reset encounter)'}`);
        await Promise.all(AdvisorRuntimeModules
            .getRuntimeModules()
            .map(module => module.clearEncounterData?.(encounterId, clearCache)));
    };
    getABTestingValue = (key) => {
        return this.getAdvisorConfig().abTesting?.[key];
    };
    getCurrentEncounterId = () => SessionKey_EncounterId.get();
    getCurrentPatientId = () => SessionKey_PatientId.get();
    getCurrentEhrInstance = (fallbackEhrInstanceId) => SessionKey_EHRInstanceId.get() ?? fallbackEhrInstanceId;
    getCurrentEHRInstallationId = () => SessionKey_EHRInstallationId.get();
    getCurrentEHREnvironmentId = () => SessionKey_EHREnvironmentId.get();
    getCurrentFacilityId = () => SessionKey_FacilityId.get();
    hasSessionData = () => ModuleFE_Session.hasSession();
    getSessionLaunchData = () => {
        if (!this.artifactMetadata)
            throw new BadImplementationException('artifactMetadata not initialized — setArtifactMetadata() was not called before getSessionLaunchData()');
        return this.artifactMetadata;
    };
    getSessionProvider = () => SessionKey_Provider.get();
    getSessionProviderId = () => SessionKey_ProviderId.get();
    getIsAvatar = () => this.isAvatar || (this.isAvatar = SessionKey_IsAvatar.get());
    getUserType = () => 'physician';
    getAdvisorConfig = () => SessionKey_AdvisorConfig.get();
    getEnableDocumentation = () => SessionKey_AdvisorConfig.get().enableDocumentation;
    getEnableAgl = () => SessionKey_AdvisorConfig.get().enableAgl;
    getUseEhrOrderId = () => SessionKey_AdvisorConfig.get().useEhrOrderId;
}
export const ModuleFE_AdvisorSession = new ModuleFE_AdvisorSession_Class();
//# sourceMappingURL=ModuleFE_AdvisorSession.js.map